import ResizeObserver from 'resize-observer-polyfill';
import {AttrsSchema, Component} from 'panel';
import isEqual from 'lodash/isEqual';
import type {Point} from 'highcharts';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {SegmentFilterController} from 'common/charts/contexts/segment-filter-controller';
import type {FormattedRow, LiftRow, RawCell} from 'common/report/retention/util/types';
import {CurrentProject} from 'common/report/model-resources/projects';
import {
  getLiftBaselineSegmentRawHead,
  getRetentionCurveTableColumnGroups,
  getUiRowsDataFromParsed,
  mapToRetentionCurveTableRows,
} from 'common/report/retention/util/parsing';
import {RetentionQueryResponse, SegmentKeyToPath} from 'common/report/retention/models/types';
import {RetentionBookmarkParams} from 'common/report/retention/util/types';
import type {LineChartAttrs, LineChartProps} from 'common/charts/visualization/line-chart';
import {DisplayAnalysisType} from 'common/charts/visualization/line-chart/enums';
import {ChartType, DEFAULT_CHART_WIDTH, REPORT_LIMITS, ReportName} from 'common/report/constants';
import {IChartDataItem, Popover} from 'common/charts/visualization/line-chart/types';
import {XAxisUnit} from 'common/charts/visualization/line-chart/enums';
import {flattenQueryResponse, retentionResponseHasData} from 'common/report/retention/util/request';
import {getBookmarkWithAppliedDefaults} from 'common/report/retention/util';
import {RetentionBookmarkParamsAndResults} from 'common/report/retention/charts/types';
import {deselectPoint, selectPoint} from 'common/report/util/highcharts';

import {getDataCellRenderer, template} from './template';
import css from './index.inline.styl';
import {bucketLabeler, computeTicks, getLineChartTooltip, percentOrAbsoluteLabel} from './line-chart-format';

interface LineChartState {
  chartRows: FormattedRow[];
  chartWidth: number; // px
  segmentKeyToPath?: SegmentKeyToPath;
  truncatedSegmentDisplayLookup?: Map<string, string>;
  formattedLiftRowsData: Map<string, LiftRow>;
  selectedHighchartsPoint?: Point;
  popoverStyles?: {top: string; left: string};
}

export interface RetentionCurveChartAttrs {
  'project-timezone'?: string;
  'utc-offset'?: number;
  'show-popover'?: boolean;
  'limit-displayed-segments'?: boolean;
  'segment-display-limit'?: number;
}

export type RetentionCurveChartProps = {
  paramsAndResults: RetentionBookmarkParamsAndResults;
  popover?: () => Popover;
};
export class RetentionCurveChart extends Component<
  LineChartState,
  RetentionCurveChartAttrs,
  any,
  any,
  ChartsContextRegistry
> {
  public popover?: () => Popover;

  formatTicksX = computeTicks();
  bucketLabeler?: (number) => string;

  private _data: RetentionQueryResponse;

  private _bookmarkParams: RetentionBookmarkParams;

  public chartResizeObserver: ResizeObserver;

  static get attrsSchema(): AttrsSchema<RetentionCurveChartAttrs> {
    return {
      'utc-offset': `number`,
      'project-timezone': `string`,
      'show-popover': `boolean`,
      'limit-displayed-segments': `boolean`,
      'segment-display-limit': `number`,
    };
  }

  get config() {
    return {
      useShadowDom: true,
      css,
      contexts: [`currentProject` as const, `segmentFilterController` as const],
      defaultContexts: {
        segmentFilterController: new SegmentFilterController(),
      },
      defaultState: {
        chartRows: [],
        chartWidth: DEFAULT_CHART_WIDTH, // px
        formattedLiftRowsData: new Map<string, LiftRow>(),
      },
      helpers: {
        hasResults: () => retentionResponseHasData(this._data),

        getChartType: () => this._bookmarkParams.chart_type,

        isFormattingTicksX: () => this.state.chartWidth > DEFAULT_CHART_WIDTH,

        getProjectTimezone: () => this.attr(`project-timezone`) || this.getCurrentProject()?.timezone,

        getUtcOffset: () => this.attr(`utc-offset`) || this.getCurrentProject()?.utcOffset,

        getChartData: (): IChartDataItem[] => {
          const {segmentKeyToPath, chartRows, formattedLiftRowsData} = this.state;

          return chartRows
            .filter((row) => this.segmentFilterController.isItemSelected(0, row.raw_head))
            .map((row) => {
              const liftRow = formattedLiftRowsData?.get(row.raw_head);
              const data = (liftRow || row).data.map(
                (cell, idx) =>
                  [idx, this._bookmarkParams.percentage_view ? cell.percent : cell.value] as [number, number],
              );

              const path = segmentKeyToPath && segmentKeyToPath[row.raw_head] ? segmentKeyToPath[row.raw_head] : [];

              return {
                id: row.raw_head,
                name: row.raw_head,
                data,
                color: row.selection_color,
                incompleteLength: getNumIncompleteCells(row),
                path,
              };

              function getNumIncompleteCells(row) {
                const incompleteIdx = row.data.findIndex((data) => data.incomplete);
                return incompleteIdx >= 0 ? row.data.length - incompleteIdx : 0;
              }
            });
        },
        getLineChartProps: (): Partial<LineChartProps> => {
          const bookmarkParams = this._bookmarkParams;
          return {
            ...(this.helpers.isFormattingTicksX() ? {formatTicksX: this.formatTicksX} : {}),
            formatXAxisLabel: this.bucketLabeler,
            formatYAxisLabel: percentOrAbsoluteLabel(bookmarkParams),
            formatTooltip: getLineChartTooltip(this, bookmarkParams),
            chartData: this.helpers.getChartData(),
            onHighChartClick: this.popover && this.helpers.handleHighChartClick,
            ...(this.popover ? {popover: this.popover} : {}),
          };
        },

        getXAxisOptions: () => {
          return {unit: XAxisUnit.Time};
        },

        getYAxisOptions: () => {
          return this._bookmarkParams.percentage_view ? {maxPadding: 0} : {};
        },

        getLineChartAttrs: (): Partial<LineChartAttrs> => ({
          'chart-width': null,
          'display-analysis-type': DisplayAnalysisType.Linear,
          'incomplete-to-date': this.helpers.isEndDateIncomplete(),
          timezone: this.helpers.getProjectTimezone(),
          'utc-offset': this.helpers.getUtcOffset(),
          'x-axis-options': this.helpers.getXAxisOptions(),
          'y-axis-options': this.helpers.getYAxisOptions(),
          'zoom-type': `x`,
        }),

        isEndDateIncomplete: () => {
          const selectedSegmentsRows = this.selectedSegmentsRows;
          if (selectedSegmentsRows.every((row) => row.data.length < 2)) {
            return false;
          }
          for (const row of selectedSegmentsRows) {
            for (const cell of row.data) {
              if (cell.incomplete) {
                return true;
              }
            }
          }
          return false;
        },

        closePopover: () => {
          deselectPoint(this.state.selectedHighchartsPoint);
          this.update({selectedHighchartsPoint: null});
        },

        handleHighChartClick: (point: Point) => {
          const {series, index, color} = point;
          const {name: segmentKey} = series;

          selectPoint(point);

          const left = series.chart.plotLeft + point.plotX + 12;
          const top = series.chart.plotTop + point.plotY + 12;
          const popoverStyles = {left: `${left}px`, top: `${top}px`};
          this.update({selectedHighchartsPoint: point, popoverStyles});

          const row = this.state.chartRows.filter((row) => row.head === segmentKey);
          const selectedRow = row[0];
          const rowIdx = this.state.chartRows.indexOf(selectedRow);
          this.dispatchEvent(
            new CustomEvent(`lineChartClick`, {
              bubbles: true,
              detail: {
                selectedRow,
                color,
                colIdx: index,
                rowIdx,
              },
            }),
          );
        },
      },
      template,
    };
  }

  private get segmentFilterController(): SegmentFilterController {
    return this.getContext(`segmentFilterController`);
  }

  get selectedSegmentsRows() {
    return this.state.chartRows.filter((row) => this.segmentFilterController.isItemSelected(0, row.raw_head));
  }

  get selectedSegmentLiftRows() {
    return this.selectedSegmentsRows.map((row) => this.state.formattedLiftRowsData.get(row.raw_head));
  }

  liftDataForSegment(row: FormattedRow): {liftRow: LiftRow; baselineRow?: FormattedRow<RawCell>} {
    if (this._bookmarkParams.lift_comparison?.groupValue) {
      const baselineRowKey = getLiftBaselineSegmentRawHead(
        row,
        this.state.segmentKeyToPath,
        this._bookmarkParams.lift_comparison,
      );
      const baselineRow = this.state.chartRows.find((row) => row.raw_head === baselineRowKey);
      const liftRow = this.state.formattedLiftRowsData.get(row.raw_head);
      return {
        baselineRow,
        liftRow,
      };
    }
    return null;
  }

  processData() {
    if (!(this._data && this._bookmarkParams && this.isConnected)) {
      return;
    }

    const {segmentKeyToPath, metadata, data, compareToPastData, comparisonMetadata, liftData} = flattenQueryResponse(
      this._data,
      this._bookmarkParams.group_by,
    );

    const {formattedUiRows, formattedLiftRowsData} = getUiRowsDataFromParsed(
      data,
      metadata,
      this._bookmarkParams,
      this.helpers.getProjectTimezone(),
      compareToPastData,
      comparisonMetadata,
      liftData,
    );

    this.update({chartRows: formattedUiRows, segmentKeyToPath, formattedLiftRowsData});

    const tableRows = mapToRetentionCurveTableRows({
      formattedUiRows,
      bookmarkParams: this._bookmarkParams,
      segmentKeyToPath,
      formattedLiftRowsData,
    });
    const tableRowLookup = new Map(tableRows.map((row) => [row.identifier, row]));

    const segmentFilterItems = formattedUiRows.map((row) => ({
      label: row.raw_head,
      id: row.raw_head,
      color: row.selection_color,
      tableRow: tableRowLookup.get(row.raw_head),
    }));

    const tableColumns = getRetentionCurveTableColumnGroups({
      formattedUiRows,
      bookmarkParams: this._bookmarkParams,
      getDataCellRenderer,
    });

    const maxNumSelections =
      this.attr(`segment-display-limit`) > 0
        ? this.attr(`segment-display-limit`)
        : REPORT_LIMITS[ReportName.Retention].segments[ChartType.RetentionTable].max;
    this.segmentFilterController.loadSections([{items: segmentFilterItems, tableColumns}], {
      defaultNumSelections: REPORT_LIMITS[ReportName.Retention].segments[ChartType.RetentionTable].default,
      maxNumSelections,
      tableSortConfig: this._bookmarkParams.sorting,
    });
    this.segmentFilterController.loadFromSavedLegend(
      this._bookmarkParams.selected_segments && [
        {selected: this._bookmarkParams.selected_segments.map((key) => ({key}))},
      ],
    );
    this.segmentFilterController.segmentationQueryLimit = REPORT_LIMITS[ReportName.Retention].query;
    this.segmentFilterController.exportSegmentationQueryLimit = REPORT_LIMITS[ReportName.Retention].csvExport;
  }

  set paramsAndResults(value: RetentionBookmarkParamsAndResults) {
    const updatedBookmarkParams = getBookmarkWithAppliedDefaults(value.params);
    const bookmarkChanged = !isEqual(this._bookmarkParams, updatedBookmarkParams);
    if (bookmarkChanged) {
      this._bookmarkParams = updatedBookmarkParams;
      this.bucketLabeler = bucketLabeler(updatedBookmarkParams);
    }
    const dataChanged = !isEqual(this._data, value.results);
    if (dataChanged) {
      this._data = value.results;
    }
    if (bookmarkChanged || dataChanged) {
      this.processData();
    }
  }

  getCurrentProject(): CurrentProject {
    return this.getContext(`currentProject`).get().value;
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
  }

  connectedCallback() {
    this.chartResizeObserver = new ResizeObserver((entries) => {
      const {width} = entries[0].contentRect;
      this.update({
        chartWidth: width,
      });
    });
    super.connectedCallback();

    //TODO add store fetch

    this.processData();
  }

  disconnectedCallback() {
    this.chartResizeObserver.disconnect();
    super.disconnectedCallback();
  }
}

export const RetentionCurveChartEl = defineAndMakeJsxFactory<{
  props: RetentionCurveChartProps;
  attrs: RetentionCurveChartAttrs;
}>(`mp-retention-curve-chart`, RetentionCurveChart);

export const RetentionCurveChartReact = panel2react<{
  props: RetentionCurveChartProps;
  attrs: RetentionCurveChartAttrs;
}>(`mp-retention-curve-chart`, RetentionCurveChart);
